import { create } from 'zustand';
import type { VehicleState, Route } from '@k-ucap/shared';
import type { TrackPoint, GisAlert, GisWaypointUpdate } from '@/domain/gis/GisMessageProjectionService.js';

export interface GisState {
  vehicles: Record<string, VehicleState>;
  tracks: Record<string, TrackPoint[]>;
  routes: Record<string, Route>;
  waypoints: Record<string, GisWaypointUpdate>;
  alerts: GisAlert[];
  selectedVehicleId: string | null;
  followVehicle: boolean;
  showTracks: boolean;
  showRoutes: boolean;
  showAlertPanel: boolean;
  autoFit: boolean;
  demoRunning: boolean;
  demoStartedAt: number | null;
}

interface GisActions {
  upsertVehicle: (vehicle: VehicleState) => void;
  addTrackPoint: (point: TrackPoint) => void;
  clearTrack: (vehicleId: string) => void;
  setRoute: (route: Route) => void;
  addWaypointUpdate: (update: GisWaypointUpdate) => void;
  addAlert: (alert: GisAlert) => void;
  acknowledgeAlert: (alertId: string) => void;
  clearAlerts: () => void;
  selectVehicle: (vehicleId: string | null) => void;
  toggleFollowVehicle: () => void;
  toggleShowTracks: () => void;
  toggleShowRoutes: () => void;
  toggleShowAlertPanel: () => void;
  setAutoFit: (value: boolean) => void;
  setDemoRunning: (running: boolean) => void;
  startDemo: () => void;
  stopDemo: () => void;
  reset: () => void;
}

const MAX_TRACK_POINTS = 500;
const TRACK_TIME_WINDOW_MS = 10 * 60 * 1000;
const MAX_ALERTS = 100;

const initialState: GisState = {
  vehicles: {},
  tracks: {},
  routes: {},
  waypoints: {},
  alerts: [],
  selectedVehicleId: null,
  followVehicle: false,
  showTracks: true,
  showRoutes: true,
  showAlertPanel: true,
  autoFit: false,
  demoRunning: false,
  demoStartedAt: null,
};

export const useGisStore = create<GisState & GisActions>((set) => ({
  ...initialState,

  upsertVehicle: (vehicle) =>
    set((state) => ({
      vehicles: { ...state.vehicles, [vehicle.vehicleId]: vehicle },
    })),

  addTrackPoint: (point) =>
    set((state) => {
      const existing = state.tracks[point.vehicleId] ?? [];
      const now = Date.now();
      const withNew = [...existing, point];
      const pruned = withNew
        .filter((p) => now - new Date(p.timestamp).getTime() <= TRACK_TIME_WINDOW_MS)
        .slice(-MAX_TRACK_POINTS);
      return {
        tracks: { ...state.tracks, [point.vehicleId]: pruned },
      };
    }),

  clearTrack: (vehicleId) =>
    set((state) => ({
      tracks: { ...state.tracks, [vehicleId]: [] },
    })),

  setRoute: (route) =>
    set((state) => ({
      routes: { ...state.routes, [route.id]: route },
    })),

  addWaypointUpdate: (update) =>
    set((state) => ({
      waypoints: { ...state.waypoints, [update.vehicleId]: update },
    })),

  addAlert: (alert) =>
    set((state) => {
      const exists = state.alerts.some((a) => a.id === alert.id);
      if (exists) return state;
      const next = [alert, ...state.alerts].slice(0, MAX_ALERTS);
      return { alerts: next };
    }),

  acknowledgeAlert: (alertId) =>
    set((state) => ({
      alerts: state.alerts.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
    })),

  clearAlerts: () => set({ alerts: [] }),

  selectVehicle: (vehicleId) => set({ selectedVehicleId: vehicleId }),

  toggleFollowVehicle: () =>
    set((state) => ({
      followVehicle: !state.followVehicle,
    })),

  toggleShowTracks: () =>
    set((state) => ({
      showTracks: !state.showTracks,
    })),

  toggleShowRoutes: () =>
    set((state) => ({
      showRoutes: !state.showRoutes,
    })),

  toggleShowAlertPanel: () =>
    set((state) => ({
      showAlertPanel: !state.showAlertPanel,
    })),

  setAutoFit: (value) => set({ autoFit: value }),

  setDemoRunning: (running) => set({ demoRunning: running, demoStartedAt: running ? Date.now() : null }),

  startDemo: () => set({ demoRunning: true, demoStartedAt: Date.now() }),

  stopDemo: () => set({ demoRunning: false, demoStartedAt: null }),

  reset: () => set(initialState),
}));
